import { Stack, Tabs, Tab, Chip, Typography, Box } from '@mui/material';
import { ScreenWrap, SearchInput } from '../../components';
import { useLocation, Link, Navigate } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import ChatList from './ChatList';
import { Api } from '../../api';
import { useAuthRoles } from '../../hooks';
import { useApiRead } from 'api-read-hook';
import { HydraCollection } from '../../types';

const CHAT_TABS = [
  {
    value: 'active',
    label: 'Active Chats',
    title: 'Active Chats',
    description:
      'These are all chats of users who are currently doing the program, and can reply to you',
    coachOnly: true,
  },
  {
    value: 'broadcast',
    label: 'Broadcasts',
    title: 'Broadcasts',
    description: "These are one-way chats, the users can't reply to you.",
  },
  {
    value: 'archived',
    label: 'Archive',
    title: 'Archived Chats',
    description: '',
  },
];

export default function ChatScreen() {
  const authRoles = useAuthRoles();
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop();
  const { getParams, setModifiedParams } = useModifiableParams();
  const params = getParams();

  const apiActive = useApiRead<HydraCollection<Api.ChatConversation.V_Active>>(
    authRoles.coach ? `/chat-conversations/clinician/active` : null,
  );
  const apiBroadcast =
    Api.ChatConversation.useReadCollectionClinicianbroadcast();
  const apiArchive = Api.ChatConversation.useReadCollectionClinicianarchived();

  const isValidTab = CHAT_TABS.some((tab) => tab.value === currentTab);
  if (!isValidTab && location.pathname === '/chat') {
    return <Navigate to="/chat/active" replace />;
  }

  const counts = {
    active: apiActive.data?.member?.length ?? 0,
    broadcast: apiBroadcast.data?.member?.length ?? 0,
    archived: apiArchive.data?.member?.length ?? 0,
  };

  const currentTabData = CHAT_TABS.find((tab) => tab.value === currentTab);

  return (
    <ScreenWrap>
      <Stack alignItems="stretch">
        <Stack spacing={1}>
          <Typography variant="h4" component="h1">
            Chats
          </Typography>

          <Tabs
            value={currentTab || 'active'}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {CHAT_TABS.filter((tab) => !tab.coachOnly || authRoles.coach).map(
              (tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Stack direction="row" alignItems="center" gap={1}>
                      {tab.label}
                      <Chip
                        label={counts[tab.value as keyof typeof counts]}
                        size="small"
                        color="default"
                      />
                    </Stack>
                  }
                  component={Link}
                  to={`/chat/${tab.value}`}
                />
              ),
            )}
          </Tabs>
        </Stack>

        <Box sx={{ maxWidth: 714 }}>
          <Box mt={3}>
            {currentTabData && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {currentTabData.title}
                </Typography>
                {currentTabData.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {currentTabData.description}
                  </Typography>
                )}

                <SearchInput
                  label="Search"
                  value={params.search || ''}
                  onChange={(e) =>
                    setModifiedParams({ search: e.target.value, page: '1' })
                  }
                  placeholder="Search by name"
                />
                <Box sx={{ height: 16 }} />
              </>
            )}

            <ChatList
              type={currentTab as 'active' | 'broadcast' | 'archived'}
              params={params}
            />
          </Box>
        </Box>
      </Stack>
    </ScreenWrap>
  );
}
